/**
 * MELANUA Auth Context
 * User authentication state management
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check auth status on mount
    const checkAuth = useCallback(async () => {
        if (!authApi.isLoggedIn()) {
            setIsLoading(false);
            return;
        }

        try {
            const userData = await authApi.me();
            if (userData) {
                setUser(userData);
                setIsAuthenticated(true);
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            authApi.logout();
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Register new user
    const register = useCallback(async (userData) => {
        try {
            const data = await authApi.register(userData);
            setUser(data.user);
            setIsAuthenticated(true);
            return { success: true, user: data.user };
        } catch (error) {
            let errorData;
            try {
                errorData = JSON.parse(error.message);
            } catch {
                errorData = { non_field_errors: [error.message] };
            }
            return { success: false, errors: errorData };
        }
    }, []);

    // Login user
    const login = useCallback(async (email, password) => {
        try {
            await authApi.login(email, password);
            const userData = await authApi.me();
            setUser(userData);
            setIsAuthenticated(true);
            return { success: true, user: userData };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, []);

    // Update profile
    const updateProfile = useCallback(async (data) => {
        const updatedUser = await authApi.updateProfile(data);
        setUser(updatedUser);
        return updatedUser;
    }, []);

    // Logout user
    const logout = useCallback(() => {
        authApi.logout();
        setUser(null);
        setIsAuthenticated(false);
    }, []);

    const value = {
        user,
        isLoading,
        isAuthenticated,
        register,
        login,
        logout,
        checkAuth,
        updateProfile,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
